import type {
    IPageCampaigns,
    IPageCheckout,
    IPageExplore,
    IPageGeneric,
    IPageHomepage,
    IPagePathFields,
    IPageProductCategoryPage,
    IPageProductListingPage,
    IPageStories,
    IPageTwoColumns,
} from "@contentful-api/types/contentful";
import type { PagePath } from "@lib/constants/pagePaths";
import { getPagePathByLocale } from "@lib/constants/pagePaths";
import { getCompleteSlug } from "@lib/utils/getCompleteSlug";
import { CMSWrapper } from "@lib/wrappers/cmsWrapper";
import type { Entry } from "contentful";
import { isEmpty } from "lodash-es";

type PageTypes =
    | IPageCampaigns
    | IPageCheckout
    | IPageExplore
    | IPageGeneric
    | IPageHomepage
    | IPageProductCategoryPage
    | IPageProductListingPage
    | IPageStories
    | IPageTwoColumns
    | undefined;

interface IGetPagePathByCompleteSlugCollection {
    slug?: string | string[];
    locale: string;
    basePath: PagePath;
    localeTagId: string;
    fallbackLocale?: string;
}

// We use getEntries instead of getEntry because we want to resolve the nested references using include
export const getPagePathByCompleteSlug = async ({
    slug = "",
    locale,
    basePath,
    localeTagId,
    fallbackLocale = "en",
}: IGetPagePathByCompleteSlugCollection): Promise<Entry<IPagePathFields>> => {
    if (!basePath) return null;

    const pagePaths = await CMSWrapper.getInstance().getEntries<IPagePathFields>({
        content_type: "pagePath",
        "fields.completeSlug": getCompleteSlug(slug, getPagePathByLocale(basePath, locale)),
        "metadata.tags.sys.id[in]": localeTagId,
        include: 10, // Include 10 levels of nested references
        limit: 1,
        locale,
    });

    let pagePath = pagePaths?.items?.[0];

    // If we don't find a pagePath for the market-specific locale, we try to find one for the fallback locale "en"
    if (isEmpty(pagePaths?.items)) {
        // Since pagePath.fields.page is not field-level translatable we need to just get the reference to the page assigned to the default locale
        const defaultPagePaths = await CMSWrapper.getInstance().getEntries<IPagePathFields>({
            content_type: "pagePath",
            "fields.completeSlug": getCompleteSlug(slug, getPagePathByLocale(basePath)),
            "metadata.tags.sys.id[in]": localeTagId,
            include: 1, // Include 1 level of nested references to insure the assigned page is published
            limit: 1,
            locale: fallbackLocale,
        });

        pagePath = defaultPagePaths?.items?.[0];

        // If we got a pagePath - and it has a page reference then we can proceed to "switch" locale,
        // if not - then this block is skipped, we return undefined - and our get*Props will handle the 404
        // pagePath.fields.page.fields.internalName check to avoid trying to get a page that is a draft
        if (pagePath && pagePath.fields.page) {
            // Then we can get the page - which is field-level translatable - with the intended locale (and resolve nested linked entries ofc)
            const translatedPageData = await CMSWrapper.getInstance().getEntry<PageTypes>(
                pagePath.fields.page.sys.id,
                {
                    locale,
                    include: 10, // now resolve linked entries,
                }
            );

            // If the locale of the page entry we requested matches our originally intended locale - then we re-assing the page entry
            if (translatedPageData?.sys?.locale === locale) {
                // cast to unknown as we cannot guarantee that the pagePath.fields.page is of the same type as the translatedPageData - though it should be :)
                pagePath.fields.page = translatedPageData as unknown as PageTypes;
            }
        }
    }

    return pagePath;
};

interface IGetPagePathCompleteSlugs {
    locale: string;
    localeTagId: string;
}

export const getAllPagePaths = async ({
    locale,
    localeTagId,
}: IGetPagePathCompleteSlugs): Promise<Entry<IPagePathFields>[]> => {
    const allPagePathsByCurrentLocaleData =
        await CMSWrapper.getInstance().getEntries<IPagePathFields>({
            content_type: "pagePath",
            "metadata.tags.sys.id[in]": localeTagId,
            include: 5,
            limit: 1000,
            locale,
        });

    return allPagePathsByCurrentLocaleData?.items || [];
};
